import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { apiErr } from "../lib/api";
import { LifeBuoy, ArrowLeft, Loader2, Paperclip, X, CheckCircle2, Mail, BookOpen } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const MAX_MB = 8;

export default function HelpPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [category, setCategory] = useState("tecnico");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const cats = ["tecnico", "facturacion", "sugerencia", "otro"];

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { setErr(t("help.errImage")); return; }
    if (f.size > MAX_MB * 1024 * 1024) { setErr(t("help.errSize", { mb: MAX_MB })); return; }
    setErr(""); setFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !message.trim()) { setErr(t("help.errRequired")); return; }
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("category", t(`help.cat.${category}`));
      fd.append("subject", subject.trim());
      fd.append("message", message.trim());
      if (file) fd.append("screenshot", file);
      await api.post("/support/contact", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSent(true);
    } catch (e2) { setErr(apiErr(e2)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-2xl mx-auto px-5 py-10">
        <Link to="/app" data-testid="help-back-link" className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-800 font-semibold mb-6">
          <ArrowLeft size={18} /> {t("help.back")}
        </Link>

        <div className="flex items-center gap-3 mb-2">
          <span className="w-11 h-11 rounded-xl bg-brand-600 text-white flex items-center justify-center"><LifeBuoy size={24} /></span>
          <h1 className="text-3xl font-bold text-slate-900">{t("help.title")}</h1>
        </div>
        <p className="text-base text-slate-600 mb-4">{t("help.subtitle")}</p>
        {/* Antes del formulario: muchas dudas se resuelven en la documentacion y esperar una
            respuesta por correo para algo ya documentado es la peor experiencia posible. */}
        <Link to="/docs" className="mb-8 flex items-center gap-3 rounded-xl border border-brand-200 bg-brand-50/70 px-4 py-3 hover:border-brand-300">
          <BookOpen size={20} className="shrink-0 text-brand-600" />
          <span className="text-sm text-slate-700">
            <span className="block font-semibold text-slate-900">Mira antes la documentación</span>
            Cómo funcionan los créditos, qué hacer si falta material y los problemas más frecuentes.
          </span>
        </Link>

        {sent ? (
          <div data-testid="help-success" className="bg-white border-2 border-green-200 rounded-2xl p-8 text-center">
            <CheckCircle2 size={48} className="mx-auto text-green-500 mb-3" />
            <h2 className="text-2xl font-bold text-slate-900 mb-2">{t("help.sentTitle")}</h2>
            <p className="text-base text-slate-600 mb-6">{t("help.sentDesc", { email: user?.email })}</p>
            <Link to="/app" className="inline-flex items-center gap-2 bg-brand-600 text-white font-bold px-6 py-3 rounded-xl hover:bg-brand-700 transition-colors">
              {t("help.backToApp")}
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white border-2 border-slate-200 rounded-2xl p-6 space-y-5" data-testid="help-form">
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-1.5">{t("help.category")}</label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="help-category" className="h-12 text-base"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {cats.map((c) => <SelectItem key={c} value={c} data-testid={`help-cat-${c}`}>{t(`help.cat.${c}`)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-bold text-slate-700 mb-1.5">{t("help.subject")}</label>
              <input data-testid="help-subject" value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={140}
                placeholder={t("help.subjectPlaceholder")}
                className="w-full border-2 border-slate-300 rounded-lg px-4 py-3 text-base bg-white focus:border-brand-600 focus:ring-4 focus:ring-brand-600/20 outline-none" />
            </div>

            <div>
              <label className="block text-sm font-bold text-slate-700 mb-1.5">{t("help.message")}</label>
              <textarea data-testid="help-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={6}
                placeholder={t("help.messagePlaceholder")}
                className="w-full border-2 border-slate-300 rounded-lg px-4 py-3 text-base bg-white focus:border-brand-600 focus:ring-4 focus:ring-brand-600/20 outline-none resize-y" />
            </div>

            <div>
              <label className="block text-sm font-bold text-slate-700 mb-1.5">{t("help.screenshot")}</label>
              {file ? (
                <div className="flex items-center gap-3 border-2 border-slate-200 rounded-lg px-4 py-3 bg-slate-50">
                  <Paperclip size={18} className="text-slate-400 shrink-0" />
                  <span className="text-sm text-slate-700 truncate flex-1" data-testid="help-file-name">{file.name}</span>
                  <button type="button" data-testid="help-file-remove" onClick={() => setFile(null)} className="text-slate-400 hover:text-red-600"><X size={18} /></button>
                </div>
              ) : (
                <label data-testid="help-file-label" className="flex items-center gap-2 border-2 border-dashed border-slate-300 rounded-lg px-4 py-3 cursor-pointer text-slate-500 hover:border-brand-400 hover:text-brand-600 transition-colors">
                  <Paperclip size={18} /> {t("help.screenshotHint")}
                  <input data-testid="help-file-input" type="file" accept="image/*" onChange={onFile} className="hidden" />
                </label>
              )}
            </div>

            {err && <p data-testid="help-error" className="text-sm text-red-600">{err}</p>}

            <button type="submit" data-testid="help-submit" disabled={busy}
              className="w-full min-h-[48px] rounded-xl bg-brand-600 text-white text-base font-bold flex items-center justify-center gap-2 hover:bg-brand-700 transition-colors disabled:opacity-60">
              {busy ? <Loader2 className="animate-spin" size={20} /> : <Mail size={20} />} {t("help.send")}
            </button>
            <p className="text-xs text-slate-400 text-center">{t("help.contextNote")}</p>
          </form>
        )}
      </div>
    </div>
  );
}
